import { toast } from "sonner";

/** Etichette campi FIC → testo per l'utente (allineate al backend). */
const FIC_FIELD_LABELS = {
  "data.entity.vat_number": "Partita IVA del destinatario",
  "data.entity.tax_code": "Codice fiscale del destinatario",
  "data.entity.name": "Nome / ragione sociale del destinatario",
  "data.entity.email": "Email del destinatario",
  "data.entity.ei_code": "Codice destinatario (SDI)",
  "data.entity.address_street": "Indirizzo del destinatario",
  "data.entity.address_postal_code": "CAP del destinatario",
  "data.entity.address_city": "Città del destinatario",
  "data.entity.address_province": "Provincia del destinatario",
  "data.date": "Data fattura",
  "data.number": "Numero progressivo",
  "data.ei_data.xml": "File XML fattura elettronica",
};

function humanizeField(path) {
  const s = (path || "").replace(/^data\./, "").replace(/_/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Campo";
}

function translateValidationMsg(msg, fieldPath) {
  const low = (msg || "").toLowerCase();
  if (low.includes("must be a string")) {
    if ((fieldPath || "").includes("vat_number")) {
      return "deve essere un testo con sole cifre (partita IVA senza prefisso IT).";
    }
    return "deve essere un testo valido.";
  }
  if (low.includes("required")) return "è obbligatorio.";
  if (low.includes("invalid")) return "non è valido.";
  return (msg || "").trim();
}

function extractJsonFromText(text) {
  const raw = (text || "").trim();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    const idx = raw.indexOf("{");
    if (idx >= 0) {
      try {
        return JSON.parse(raw.slice(idx));
      } catch {
        return null;
      }
    }
  }
  return null;
}

/**
 * @param {string} raw - messaggio grezzo (es. ultimo_errore_sdi o risposta API)
 * @param {{ userMessage?: string }} [options]
 * @returns {{ summary: string, lines: string[], technical: string }}
 */
export function parseSdiError(raw, options = {}) {
  const userMessage = (options.userMessage || "").trim();
  const technical = (raw || "").trim();

  if (userMessage) {
    const lines = userMessage.includes("\n")
      ? userMessage.split("\n").map((l) => l.trim()).filter(Boolean)
      : [userMessage];
    return {
      summary: lines[0],
      lines,
      technical: technical || userMessage,
    };
  }

  const lines = [];
  let body = technical;

  const ficPrefix = /^Errore API (?:FIC|Fatture in Cloud) \(HTTP (\d+)\):\s*/i;
  const m = body.match(ficPrefix);
  const httpStatus = m ? Number(m[1]) : null;
  if (m) body = body.slice(m[0].length);

  const payload = extractJsonFromText(body);
  if (payload && typeof payload === "object") {
    const err = payload.error && typeof payload.error === "object" ? payload.error : payload;
    const main = (err.message || "").trim();
    if (main && !/^invalid request\.?$/i.test(main)) {
      lines.push(main);
    }
    const vr = err.validation_result;
    if (vr && typeof vr === "object") {
      Object.entries(vr).forEach(([field, msgs]) => {
        const label = FIC_FIELD_LABELS[field] || humanizeField(field);
        const arr = Array.isArray(msgs) ? msgs : [msgs];
        arr.forEach((item) => {
          if (item) lines.push(`${label}: ${translateValidationMsg(String(item), field)}`);
        });
      });
    }
  }

  if (!lines.length) {
    if (httpStatus === 422) {
      lines.push(
        "Fatture in Cloud ha rifiutato i dati della fattura. Verifica anagrafica e dati fiscali del destinatario e riprova.",
      );
    } else if (httpStatus === 401) {
      lines.push("Autenticazione Fatture in Cloud non valida. Controlla il token in Impostazioni.");
    } else if (technical) {
      lines.push("Invio allo SDI non riuscito. Controlla i dati della fattura o riprova più tardi.");
    } else {
      lines.push("Invio allo SDI non riuscito.");
    }
  }

  return {
    summary: lines[0],
    lines,
    technical: technical || lines.join("\n"),
  };
}

/** Toast sonner con messaggio leggibile e azione per il dettaglio tecnico. */
export function toastSdiError(raw, options = {}) {
  const parsed = parseSdiError(raw, options);
  const hasTech = parsed.technical && parsed.technical !== parsed.summary;

  toast.error(parsed.summary, {
    description: parsed.lines.length > 1 ? parsed.lines.slice(1, 4).join(" · ") : undefined,
    duration: 12000,
    ...(hasTech
      ? {
          action: {
            label: "Dettaglio tecnico",
            onClick: () => {
              toast.info("Dettaglio tecnico SDI", {
                description: parsed.technical,
                duration: 30000,
              });
            },
          },
        }
      : {}),
  });
}
